import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { supabase, saveContent } from '@/integrations/supabase/client'

const CONSENT_KEY = 'sb_cookie_consent_v2'

function getConsent(): Record<string, boolean> | null {
  try { return JSON.parse(localStorage.getItem(CONSENT_KEY) ?? 'null') } catch { return null }
}

export function CookieBanner() {
  const [visible, setVisible] = useState(false)
  const [customizing, setCustomizing] = useState(false)
  const [analytics, setAnalytics] = useState(true)
  const [marketing, setMarketing] = useState(false)

  useEffect(() => {
    const t = setTimeout(() => { if (getConsent() === null) setVisible(true) }, 1200)
    return () => clearTimeout(t)
  }, [])

  function save(prefs: Record<string, boolean>) {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(prefs))
    setVisible(false)
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-50"
        >
          <div className="bg-card border border-border rounded-xl p-5 shadow-2xl">
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-semibold text-sm">Gestion des cookies</h3>
              <button onClick={() => save({ essential: true, analytics: false, marketing: false })}
                className="text-muted-foreground hover:text-foreground ml-2"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Nous utilisons des cookies pour améliorer votre expérience.</p>
            {customizing && (
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                  <span>Essentiels <span className="text-muted-foreground">(toujours actifs)</span></span>
                  <span className="text-primary">✓</span>
                </div>
                {[
                  { label: 'Analytiques', val: analytics, set: setAnalytics },
                  { label: 'Marketing', val: marketing, set: setMarketing },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between text-xs">
                    <span>{item.label}</span>
                    <button onClick={() => item.set(v => !v)}
                      className={`w-9 h-5 rounded-full transition-colors ${item.val ? 'bg-primary' : 'bg-muted'} relative`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${item.val ? 'left-4' : 'left-0.5'}`} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {customizing
                ? <Button size="sm" onClick={() => save({ essential: true, analytics, marketing })}
                    className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs">Enregistrer</Button>
                : <Button size="sm" variant="outline" onClick={() => setCustomizing(true)} className="text-xs">Personnaliser</Button>
              }
              <Button size="sm" variant="outline" onClick={() => save({ essential: true, analytics: false, marketing: false })} className="text-xs">Tout refuser</Button>
              <Button size="sm" onClick={() => save({ essential: true, analytics: true, marketing: true })}
                className="bg-primary hover:bg-primary/90 text-primary-foreground text-xs">Tout accepter</Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CookieBanner

// Export supabase pour éviter l'import inutilisé
export { supabase, saveContent }
